import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useAlert } from '../../../constants/AlertProvider'
import Cookies from 'js-cookie'
import { Country, State, City } from "country-state-city";
import { AtSignIcon, PhoneIcon, MapPinIcon, UserIcon } from 'lucide-react'

export default function EditPersonalInfo({ address, setAddress }) {
    const [countries] = useState(Country.getAllCountries());
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [street, setStreet] = useState("");
    const [zip, setZip] = useState("");
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedState, setSelectedState] = useState(null);
    const [selectedCity, setSelectedCity] = useState("");
    const [userInfo, setUserInfo] = useState({
        name: '',
        email: '',
        phone_number: '',
        address: '',
    })
    const showAlert = useAlert();

    // Fetch user info on mount
    useEffect(() => {
        const fetchPersonalInfo = async () => {
            try {
                const userId = Cookies.get("id");
                const res = await axios.get(`/api/users/${userId}/`);
                setUserInfo({
                    name: res.data.name || '',
                    email: res.data.email || '',
                    phone_number: res.data.phone_number || '',
                    address: res.data.address || '',
                });

                // Parse address into fields if present
                if (res.data.address) {
                    const parts = res.data.address.split(", ");
                    if (parts.length >= 4) {
                        setStreet(parts[0]);
                        setZip(parts[parts.length - 1]);
                        const city = parts[parts.length - 3];
                        const stateName = parts[parts.length - 2];
                        setSelectedCity(city);

                        // Find country and state objects
                        const country = countries.find((c) =>
                            State.getStatesOfCountry(c.isoCode).some((s) => s.name === stateName)
                        );
                        if (country) {
                            setSelectedCountry(country);
                            const stateList = State.getStatesOfCountry(country.isoCode);
                            setStates(stateList);
                            const state = stateList.find((s) => s.name === stateName);
                            if (state) {
                                setSelectedState(state);
                                setCities(City.getCitiesOfState(country.isoCode, state.isoCode));
                            }
                        }
                    }
                }
            } catch (e) {
                showAlert("Failed to load personal info", "error");
                console.error("Failed to load personal info", e);
            }
        }
        fetchPersonalInfo();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (street && selectedCity && selectedState && zip && selectedCountry) {
            const fullAddress = `${street}, ${selectedCity}, ${selectedState.name}, ${zip}`;
            setAddress(fullAddress); // <-- Only update local state, not backend
        }
        // eslint-disable-next-line
    }, [street, selectedCity, selectedState, zip, selectedCountry]);

    // Update user info field (except address)
    const updateUserInfo = async (field, value) => {
        const userId = Cookies.get("id");
        setUserInfo(prev => ({ ...prev, [field]: value }));
        try {
            await axios.patch(
                `/api/users/${userId}/`,
                { [field]: value },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': Cookies.get('csrftoken'),
                    },
                    withCredentials: true,
                }
            );
            showAlert("Personal info updated", "success");
        } catch (e) {
            showAlert("Failed to update personal info", "error");
        }
    };

    // Handlers for country/state/city changes
    const handleCountryChange = (isoCode) => {
        const country = countries.find((c) => c.isoCode === isoCode);
        setSelectedCountry(country);
        const stateList = State.getStatesOfCountry(isoCode);
        setStates(stateList);
        setSelectedState(null);
        setCities([]);
        setSelectedCity("");
    };

    const handleStateChange = (isoCode) => {
        const state = states.find((s) => s.isoCode === isoCode);
        setSelectedState(state);
        if (selectedCountry) {
            setCities(City.getCitiesOfState(selectedCountry.isoCode, isoCode));
        } else {
            setCities([]);
        }
        setSelectedCity("");
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Full Name
                </label>
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <UserIcon size={18} className="text-gray-400 dark:text-gray-500" />
                    </div>
                    <input
                        type="text"
                        value={userInfo.name}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        onChange={e => updateUserInfo('name', e.target.value)}
                    />
                </div>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email Address
                </label>
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <AtSignIcon size={18} className="text-gray-400 dark:text-gray-500" />
                    </div>
                    <input
                        type="email"
                        value={userInfo.email}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        onChange={e => updateUserInfo('email', e.target.value)}
                        disabled
                    />
                </div>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Phone Number
                </label>
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <PhoneIcon size={18} className="text-gray-400 dark:text-gray-500" />
                    </div>
                    <input
                        type="tel"
                        value={userInfo.phone_number}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        onChange={e => updateUserInfo('phone_number', e.target.value)}
                    />
                </div>
            </div>
            <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Street Address</label>
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MapPinIcon size={18} className="text-gray-400 dark:text-gray-500" />
                    </div>
                    <input
                        type="text"
                        value={street}
                        onChange={(e) => setStreet(e.target.value)}
                        placeholder="Enter street address"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
            </div>
            {/* Country */}
            <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Country</label>
                <select
                    value={selectedCountry?.isoCode || ""}
                    onChange={(e) => handleCountryChange(e.target.value)}
                    className="block w-full pl-3 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                    <option value="">Select a country</option>
                    {countries.map((country) => (
                        <option key={country.isoCode} value={country.isoCode}>
                            {country.name}
                        </option>
                    ))}
                </select>
            </div>
            {/* State, City, ZIP */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">State</label>
                    <select
                        value={selectedState?.isoCode || ""}
                        onChange={(e) => handleStateChange(e.target.value)}
                        disabled={!selectedCountry}
                        className="block w-full pl-3 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="">Select a state</option>
                        {states.map((state) => (
                            <option key={state.isoCode} value={state.isoCode}>
                                {state.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">City</label>
                    <select
                        value={selectedCity}
                        onChange={(e) => setSelectedCity(e.target.value)}
                        disabled={!selectedState}
                        className="block w-full pl-3 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="">Select a city</option>
                        {cities.map((city) => (
                            <option key={city.name} value={city.name}>
                                {city.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">ZIP / Postal Code</label>
                    <input
                        type="text"
                        value={zip}
                        onChange={(e) => setZip(e.target.value)}
                        placeholder="Enter ZIP code"
                        className="block w-full pl-3 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
            </div>
        </div>
    )
}